/**
 * PolicyEditorModal - Create/Edit Policy Modal
 *
 * Large modal with tabs for Visual Builder, JSON Editor, and Preview.
 * Handles policy creation and editing with comprehensive validation.
 */

import MonacoJsonEditor from '@/components/common/MonacoJsonEditor';
import RefreshOverlay from '@/components/common/RefreshOverlay';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';
import type { Entity, Policy, PolicyDocument, PolicyFormData } from '@/types/policy';
import { getErrorMessage } from '@/utils/errorHandling';
import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PolicyBuilder from './PolicyBuilder';
import PolicyPreview from './PolicyPreview';

interface PolicyEditorModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  policy?: Policy | null; // If editing, otherwise creating
  studies?: Entity[];
  clinics?: Entity[];
  devices?: Entity[];
  dataTypes?: Entity[];
  users?: Entity[];
  userGroups?: Entity[];
  roles?: Entity[];
  onPolicyCreated?: (policyId: number) => void | Promise<void>; // Optional callback when policy is created
  onRefreshStudies?: () => void | Promise<void>;
  onRefreshClinics?: () => void | Promise<void>;
  onRefreshDevices?: () => void | Promise<void>;
  onRefreshDataTypes?: () => void | Promise<void>;
  onRefreshUsers?: () => void | Promise<void>;
  onRefreshUserGroups?: () => void | Promise<void>;
  onRefreshRoles?: () => void | Promise<void>;
  isRefreshingStudies?: boolean;
  isRefreshingClinics?: boolean;
  isRefreshingDevices?: boolean;
  isRefreshingDataTypes?: boolean;
  isRefreshingUsers?: boolean;
  isRefreshingUserGroups?: boolean;
  isRefreshingRoles?: boolean;
}

export default function PolicyEditorModal({
  show,
  onHide,
  onSuccess,
  policy,
  studies = [],
  clinics = [],
  devices = [],
  dataTypes = [],
  users = [],
  userGroups = [],
  roles = [],
  onPolicyCreated,
  onRefreshStudies,
  onRefreshClinics,
  onRefreshDevices,
  onRefreshDataTypes,
  onRefreshUsers,
  onRefreshUserGroups,
  onRefreshRoles,
  isRefreshingStudies = false,
  isRefreshingClinics = false,
  isRefreshingDevices = false,
  isRefreshingDataTypes = false,
  isRefreshingUsers = false,
  isRefreshingUserGroups = false,
  isRefreshingRoles = false,
}: PolicyEditorModalProps) {
  const { t } = useTranslation();
  const isEditing = !!policy;

  // Handler to refresh all entity data with overlay
  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      if (onRefreshStudies) {
        setRefreshStatus('studies');
        await Promise.resolve(onRefreshStudies());
      }
      if (onRefreshClinics) {
        setRefreshStatus('clinics');
        await Promise.resolve(onRefreshClinics());
      }
      if (onRefreshDevices) {
        setRefreshStatus('devices');
        await Promise.resolve(onRefreshDevices());
      }
      if (onRefreshDataTypes) {
        setRefreshStatus('data types');
        await Promise.resolve(onRefreshDataTypes());
      }
      if (onRefreshUsers) {
        setRefreshStatus('users');
        await Promise.resolve(onRefreshUsers());
      }
      if (onRefreshUserGroups) {
        setRefreshStatus('user groups');
        await Promise.resolve(onRefreshUserGroups());
      }
      if (onRefreshRoles) {
        setRefreshStatus('roles');
        await Promise.resolve(onRefreshRoles());
      }
      toast.success('Data refreshed successfully');
    } catch (err) {
      console.error('Refresh failed:', err);
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
      setRefreshStatus('');
    }
  };

  const [activeTab, setActiveTab] = useState<'visual' | 'json' | 'preview'>('visual');
  const [formData, setFormData] = useState<PolicyFormData>({
    name: '',
    description: '',
    policy_document: {
      Version: '2025-01-01',
      Statement: [],
    },
    is_template: false,
  });

  const [jsonValue, setJsonValue] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Refresh overlay state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<string>('');

  // Initialize form when modal opens or policy changes
  useEffect(() => {
    if (show) {
      if (policy) {
        // Editing existing policy
        setFormData({
          name: policy.name,
          description: policy.description || '',
          policy_document: policy.policy_document,
          is_template: policy.is_template,
        });
        setJsonValue(JSON.stringify(policy.policy_document, null, 2));
      } else {
        // Creating new policy
        setFormData({
          name: '',
          description: '',
          policy_document: {
            Version: '2025-01-01',
            Statement: [],
          },
          is_template: false,
        });
        setJsonValue(JSON.stringify({ Version: '2025-01-01', Statement: [] }, null, 2));
      }
      setValidationErrors([]);
      setActiveTab('visual');
    }
  }, [show, policy]);

  // Sync JSON when switching from visual to JSON tab
  const handleTabSelect = async (key: string | null) => {
    if (!key) return;

    if (key === 'json' && activeTab === 'visual') {
      // Sync visual builder to JSON editor
      setJsonValue(JSON.stringify(formData.policy_document, null, 2));
    } else if (key === 'visual' && activeTab === 'json') {
      // Sync JSON editor to visual builder (with validation)
      try {
        const parsed = JSON.parse(jsonValue);
        setFormData({ ...formData, policy_document: parsed });
        setValidationErrors([]);
      } catch (error) {
        setValidationErrors(['Invalid JSON format']);
        return; // Don't switch tabs if JSON is invalid
      }
    } else if (key === 'preview') {
      // Sync current state to preview
      if (activeTab === 'json') {
        try {
          const parsed = JSON.parse(jsonValue);
          setFormData({ ...formData, policy_document: parsed });
          setValidationErrors([]);
        } catch (error) {
          setValidationErrors(['Invalid JSON format. Cannot preview.']);
          return;
        }
      }
    }

    setActiveTab(key as 'visual' | 'json' | 'preview');
  };

  // Validate policy document with backend
  const validatePolicy = async (): Promise<boolean> => {
    setIsValidating(true);
    setValidationErrors([]);

    try {
      // Get current policy document
      let policyDoc: PolicyDocument;
      if (activeTab === 'json') {
        try {
          policyDoc = JSON.parse(jsonValue);
        } catch (error) {
          setValidationErrors(['Invalid JSON format']);
          setIsValidating(false);
          return false;
        }
      } else {
        policyDoc = formData.policy_document;
      }

      // Call backend validation endpoint
      const response = await apiClient.validatePolicy({
        policy_document: policyDoc,
      });

      if (response.error) {
        setValidationErrors([response.error]);
        setIsValidating(false);
        return false;
      }

      setIsValidating(false);
      return true;
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error) || 'Validation failed';
      setValidationErrors([errorMessage]);
      setIsValidating(false);
      return false;
    }
  };

  // Save policy
  const handleSave = async () => {
    // Basic validation
    if (!formData.name.trim()) {
      setValidationErrors(['Policy name is required']);
      return;
    }

    if (formData.name.length > 255) {
      setValidationErrors(['Policy name must be 255 characters or less']);
      return;
    }

    // Sync JSON to form data if on JSON tab
    if (activeTab === 'json') {
      try {
        const parsed = JSON.parse(jsonValue);
        setFormData({ ...formData, policy_document: parsed });
      } catch (error) {
        setValidationErrors(['Invalid JSON format']);
        return;
      }
    }

    // Validate policy document
    const isValid = await validatePolicy();
    if (!isValid) {
      toast.error(t('admin.policyManagement.validationFailed', 'Policy validation failed'));
      return;
    }

    // Save policy
    setIsSaving(true);
    try {
      let response;

      if (isEditing && policy) {
        // Update existing policy
        response = await apiClient.updatePolicy(policy.id, {
          name: formData.name,
          description: formData.description || null,
          policy_document: formData.policy_document,
          is_template: formData.is_template,
        });
      } else {
        // Create new policy
        response = await apiClient.createPolicy({
          name: formData.name,
          description: formData.description || null,
          policy_document: formData.policy_document,
          is_template: formData.is_template,
        });
      }

      if (response.error) {
        toast.error(response.error);
        setValidationErrors([response.error]);
        setIsSaving(false);
        return;
      }

      toast.success(
        isEditing
          ? t('admin.policyManagement.policyUpdated', 'Policy updated successfully')
          : t('admin.policyManagement.policyCreated', 'Policy created successfully')
      );

      // If this is a new policy and we have a callback, call it with the policy ID
      if (!isEditing && response.data) {
        const createdPolicy = response.data as { id: number };
        if (onPolicyCreated && createdPolicy.id) {
          await onPolicyCreated(createdPolicy.id);
        }
      }

      setIsSaving(false);
      onSuccess();
      onHide();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error) || 'Failed to save policy';
      toast.error(errorMessage);
      setValidationErrors([errorMessage]);
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false} scrollable>
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <Modal.Title>
            {isEditing
              ? t('admin.policyManagement.editPolicy', 'Edit Policy')
              : t('admin.policyManagement.createPolicy', 'Create Policy')}
          </Modal.Title>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleRefreshAll}
            disabled={isRefreshing}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </Button>
        </div>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', position: 'relative' }}>
        <RefreshOverlay isRefreshing={isRefreshing} currentItem={refreshStatus} />
        {/* Basic Fields */}
        <Form.Group className="mb-3">
          <Form.Label>
            {t('admin.policyManagement.policyName', 'Policy Name')}{' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('admin.policyManagement.policyNamePlaceholder', 'Enter policy name')}
            maxLength={255}
            required
          />
          <Form.Text className="text-muted">
            {t('admin.policyManagement.policyNameHelp', 'Unique name for this policy (max 255 characters)')}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{t('admin.policyManagement.description', 'Description')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t(
              'admin.policyManagement.descriptionPlaceholder',
              'Optional description of what this policy does'
            )}
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Check
            type="checkbox"
            id="is-template"
            label={t('admin.policyManagement.isTemplate', 'Mark as template policy')}
            checked={formData.is_template}
            onChange={(e) => setFormData({ ...formData, is_template: e.target.checked })}
          />
          <Form.Text className="text-muted">
            {t(
              'admin.policyManagement.isTemplateHelp',
              'Template policies can be cloned to create similar policies'
            )}
          </Form.Text>
        </Form.Group>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert variant="danger" dismissible onClose={() => setValidationErrors([])}>
            <Alert.Heading>
              {t('admin.policyManagement.validationErrors', 'Validation Errors')}
            </Alert.Heading>
            <ul className="mb-0">
              {validationErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Policy Document Tabs */}
        <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="mb-3">
          <Tab
            eventKey="visual"
            title={t('admin.policyManagement.visualBuilder', 'Visual Builder')}
          >
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <PolicyBuilder
                onPolicyChange={(policy) =>
                  setFormData({ ...formData, policy_document: policy })
                }
                initialPolicy={formData.policy_document}
                studies={studies}
                clinics={clinics}
                devices={devices}
                dataTypes={dataTypes}
                users={users}
                userGroups={userGroups}
                roles={roles}
                onRefreshStudies={onRefreshStudies}
                onRefreshClinics={onRefreshClinics}
                onRefreshDevices={onRefreshDevices}
                onRefreshDataTypes={onRefreshDataTypes}
                onRefreshUsers={onRefreshUsers}
                onRefreshUserGroups={onRefreshUserGroups}
                onRefreshRoles={onRefreshRoles}
                isRefreshingStudies={isRefreshingStudies}
                isRefreshingClinics={isRefreshingClinics}
                isRefreshingDevices={isRefreshingDevices}
                isRefreshingDataTypes={isRefreshingDataTypes}
                isRefreshingUsers={isRefreshingUsers}
                isRefreshingUserGroups={isRefreshingUserGroups}
                isRefreshingRoles={isRefreshingRoles}
              />
            </div>
          </Tab>

          <Tab eventKey="json" title={t('admin.policyManagement.jsonEditor', 'JSON Editor')}>
            <Form.Group>
              <Form.Label>
                {t('admin.policyManagement.policyDocument', 'Policy Document (JSON)')}
              </Form.Label>
              <MonacoJsonEditor
                value={jsonValue}
                onChange={setJsonValue}
                height={500}
                placeholder={JSON.stringify(
                  { Version: '2025-01-01', Statement: [] },
                  null,
                  2
                )}
              />
              <Form.Text className="text-muted">
                {t(
                  'admin.policyManagement.jsonEditorHelp',
                  'Edit the policy document as JSON. Must be valid JSON format.'
                )}
              </Form.Text>
            </Form.Group>
          </Tab>

          <Tab eventKey="preview" title={t('admin.policyManagement.preview', 'Preview')}>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <PolicyPreview
                policy={formData.policy_document}
                studies={studies}
                clinics={clinics}
                devices={devices}
                dataTypes={dataTypes}
              />
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving || isValidating}>
          {t('admin.policyManagement.cancel', 'Cancel')}
        </Button>
        <Button
          variant="outline-primary"
          onClick={validatePolicy}
          disabled={isSaving || isValidating}
        >
          {isValidating
            ? t('admin.policyManagement.validating', 'Validating...')
            : t('admin.policyManagement.validate', 'Validate')}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving || isValidating}>
          {isSaving
            ? t('admin.policyManagement.saving', 'Saving...')
            : isEditing
              ? t('admin.policyManagement.updatePolicy', 'Update Policy')
              : t('admin.policyManagement.createPolicy', 'Create Policy')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
